import React, { useState, useEffect, useRef } from 'react'
import imageCacheService from '../../services/ImageCacheService'

/**
 * ImageComponent - Smart image component with caching and lazy loading
 * 
 * Features:
 * - Automatic caching via ImageCacheService
 * - Lazy loading with Intersection Observer
 * - Error handling with fallback images
 * - Support for custom styling
 */
const ImageComponent = ({
    src,
    alt = '',
    lazy = true,
    fallback = '/icons/No-postimage-icon.jpeg',
    onLoad,
    onError,
    className = '',
    style = {},
    ...otherProps
}) => {
    // Use original src immediately, cache in background
    const [imageSrc, setImageSrc] = useState(src || fallback)
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const imgRef = useRef(null)
    const observerRef = useRef(null)
    const isMountedRef = useRef(true)

    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
        }
    }, [])

    useEffect(() => {
        if (!src) {
            setHasError(true)
            setIsLoading(false)
            setImageSrc(fallback)
            return
        }

        // Set original src immediately so image shows
        setImageSrc(src)
        setIsLoading(false)

        // Cache the image in background (don't wait for it)
        const cacheImage = async () => {
            try {
                // This caches the image but we don't use the blob URL
                // to avoid CORS and blob URL issues
                await imageCacheService.getCachedImage(src)
            } catch (error) {
                console.error('Failed to cache image:', error)
            }
        }

        // If lazy loading is enabled, use Intersection Observer
        if (lazy && 'IntersectionObserver' in window) {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            cacheImage()
                            // Stop observing once triggered
                            if (observerRef.current && imgRef.current) {
                                observerRef.current.unobserve(imgRef.current)
                            }
                        }
                    })
                },
                {
                    rootMargin: '50px', // Start loading 50px before image enters viewport
                    threshold: 0.01
                }
            )

            if (imgRef.current) {
                observerRef.current.observe(imgRef.current)
            }

            return () => {
                if (observerRef.current && imgRef.current) {
                    observerRef.current.unobserve(imgRef.current)
                }
            }
        } else {
            // Cache immediately if lazy loading is disabled
            cacheImage()
        }
    }, [src, lazy, fallback])

    const handleLoad = (e) => {
        if (isMountedRef.current) {
            setIsLoading(false)
        }
        if (onLoad) {
            onLoad(e)
        }
    }

    const handleError = (e) => {
        if (isMountedRef.current) {
            setHasError(true)
            setIsLoading(false)
            setImageSrc(fallback)
        }
        if (onError) {
            onError(e)
        }
    }

    return (
        <img
            ref={imgRef}
            src={imageSrc}
            alt={alt}
            className={className}
            style={style}
            onLoad={handleLoad}
            onError={handleError}
            loading={lazy ? 'lazy' : 'eager'}
            {...otherProps}
        />
    )
}

export default ImageComponent
